﻿import {translation} from "@2d-front-monorepo/core";
import {useRemoteData} from "@2d-front-monorepo/design-library";
import {
	AddressComponent,
	Drivers, getHiddenResidencyProperties, PermitStatusComponent,
	Residencies, ResidencyUses,
	Tariffs,
	Vehicles,
	Zones
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useMemo} from "react";
import PermitsInlineLoading from "../../components/PermitsInlineLoading";
import {DefaultPermitProps} from "../../customers/Registrations";
import {formatDate, formatTime} from "../../functions";
import {useGlobalContext} from "../../hooks/useGlobalContext";
import {useHttpClient} from "../../hooks/useHttpClient";
import {usePermits} from "../../hooks/usePermits";
import {TotalComponent} from "./ViewComponents/TotalComponent";

function Permit(props: DefaultPermitProps): JSX.Element {
	const {group, residency, vehicles, tariff, zone} = props;
	const {permit} = group;
	const grouped = group.count > 1;
	const {installation} = useGlobalContext();
	const klass = useMemo(() => "card" + (grouped ? " card--stacked" : ""), [grouped]);
	const hiddenResidencyProperties = getHiddenResidencyProperties(installation, residency?.original?.entity?.uses ?? ResidencyUses.None);

	return (
		<div className="card--stacked-wrapper">
			<section className={klass}>
				<header className="layout horizontal aligned middle" id={"p_" + permit.id}>
					<div className="flex">
						<div className="columns gap">
							<dl className="permit__date col50">
								<dt>{translation(PermitTranslations.start_date)}</dt>
								<dd>
									<span>{formatDate(group.startDate.toDateOnly())}</span>
									<span>{formatTime(group.startDate.toTimeOnly())}</span>
								</dd>
							</dl>
							<dl className="permit__date col50">
								<dt>{translation(PermitTranslations.end_date)}</dt>
								<dd>
									<span>{group.endDate && formatDate(group.endDate.toDateOnly())}</span>
									<span>{group.endDate && formatTime(group.endDate.toTimeOnly())}</span>
								</dd>
							</dl>
						</div>
					</div>
					<PermitStatusComponent permit={permit}/>
				</header>
				<hr className="no-margin"/>
				<div className="columns gap padded">
					<dl className="title-desc col25">
						<dt>{translation(PermitTranslations.zone)}</dt>
						<dd>{zone.name || zone.id}</dd>
					</dl>

					<dl className="title-desc col25">
						<dt>{translation(PermitTranslations.tariff)}</dt>
						<dd>{tariff.title}</dd>
					</dl>

					{residency && <dl className="title-desc col25">
                        <dt>{translation(PermitTranslations.address)}</dt>
                        <dd>
                            <AddressComponent value={residency.original.entity} hiddenEntityProperties={hiddenResidencyProperties}/>
                        </dd>
                    </dl>}

					{vehicles.length > 0 &&
                        <dl className="title-desc col25">
                            <dt>{translation(PermitTranslations.vehicles)}</dt>
                            <dd>
								{vehicles.map(((v, i) => <span key={i}>{v.original.entity.plate}</span>))}
                            </dd>
                        </dl>}
				</div>
				<TotalComponent permit={permit}/>
			</section>
		</div>);
}

export default function ArchivedPermits(): JSX.Element {
	const {permits, holderData} = usePermits();
	const installation = useGlobalContext();
	const {data, loading} = useRemoteData(() => useHttpClient().permits.archived());

	if (loading) {
		return <PermitsInlineLoading/>;
	}

	return (
		<div className="layout vertical gap">
			{data.map(
				permit => {
					const tariff = Tariffs.get(holderData, permit.tariffId);
					const driver = Drivers.getChanged(permits, permit.driver);
					const residency = Residencies.getChanged(permits, permit.residency);
					const vehicles = Vehicles.getChanged(permits, permit.vehicles);
					const zone = Zones.get(holderData, residency == null ? permit.parkingLocation.zoneId : residency.original.entity.zoneId);
					const group = {
						name: "",
						startDate: permit.startDate,
						endDate: permit.endDate,
						permit: permit,
						permits: [],
						count: 1
					}

					return <Permit key={permit.id}
					               group={group}
					               permits={permits}
					               holderData={holderData}
					               residency={residency}
					               driver={driver}
					               vehicles={vehicles}
					               zone={zone}
					               tariff={tariff}
					               now={installation.now}
					/>
				})
			}
		</div>
	);
}
